/**
 * The single owner of the URL⇄locale format.
 *
 * Language lives in the URL path as a leading `/en` or `/ar` segment — that is the
 * source of truth for the active language (localStorage is only a hint for steering
 * a returning visitor). Every module that needs to read or build a locale-prefixed
 * URL goes through the helpers here, so the format is defined in exactly one place.
 */

export type Locale = "en" | "ar";

export const LOCALES: Locale[] = ["en", "ar"];
export const DEFAULT_LOCALE: Locale = "en";

/** The locale a pathname declares, or null when it carries no locale prefix. */
export function localeFromPath(pathname: string): Locale | null {
  const seg = pathname.replace(/^\/+/, "").split("/")[0];
  return (LOCALES as string[]).includes(seg) ? (seg as Locale) : null;
}

/** Strip a leading `/en` or `/ar`, returning the locale-less remainder ("/fleet"). */
export function stripLocale(pathname: string): string {
  const loc = localeFromPath(pathname);
  if (!loc) return pathname || "/";
  const rest = pathname.slice(`/${loc}`.length);
  return rest === "" ? "/" : rest;
}

/** Build a locale-prefixed URL from a locale-less path ("/fleet" → "/ar/fleet"). */
export function withLocale(loc: Locale, path: string): string {
  const clean = path === "/" ? "" : path;
  return `/${loc}${clean}`;
}
